import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './Button';
import { Lines } from './Lines';

/**
 * 오류 경계 (44회차).
 *
 * 43회차까지는 경계가 하나도 없어, 렌더 중 예외 하나가 루트를 통째로 내렸다. 실시간 화면은
 * SSE 로 온 값을 그대로 그리므로 모양이 틀린 보고 한 장, 또는 어느 화면의 `undefined` 접근
 * 하나가 머리·탭까지 지웠다. 관제 화면이 하얗게 되면 "관제가 죽었다"와 구별이 안 된다.
 *
 * `App`이 두 겹으로 쓴다 — 화면 본문(탭 아래)과 앱 전체. 본문 경계는 머리·탭을 살려 둬서
 * 다른 화면으로 옮길 수 있게 하고, `resetKey`(화면 열쇠)가 바뀌면 저절로 풀린다.
 * 경계는 자식을 새로 만들지 않는다 — 열쇠를 `key`로 쓰면 조건만 바뀌어도 화면 상태가 날아간다.
 */
export class ErrorBoundary extends Component<
  {
    children: ReactNode;
    /** 이 값이 바뀌면 오류 상태를 푼다. 화면을 옮겼다는 뜻이다 */
    resetKey?: string;
    /** 브라우저 콘솔에 남길 자리 이름 */
    where?: string;
  },
  { error?: Error }
> {
  override state: { error?: Error } = {};

  static getDerivedStateFromError(error: unknown): { error: Error } {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(
      `화면을 그리다 예외가 났다 (${this.props.where ?? '화면'})`,
      error,
      info.componentStack,
    );
  }

  override componentDidUpdate(previous: { resetKey?: string }): void {
    if (this.state.error !== undefined && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: undefined });
    }
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === undefined) return this.props.children;
    return <RenderFailure error={error} onRetry={() => this.setState({ error: undefined })} />;
  }
}

/** 경계가 대신 그리는 것. 무엇이 잘못됐고 어떻게 하면 되는지를 적는다 */
function RenderFailure({ error, onRetry }: { error: Error; onRetry: () => void }) {
  return (
    <div role="alert" className="rounded-xl border border-error/40 bg-error/5 p-5 text-sm">
      <p className="font-semibold text-error">이 화면을 그리다 오류가 났다</p>
      <p className="mt-1 text-muted">
        <Lines
          lines={[
            '다시 그리기를 누르거나 브라우저를 새로고침할 것.',
            '되풀이되면 관제 화면과 관제 서버를 같은 판으로 맞췄는지 확인할 것',
          ]}
        />
      </p>
      <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-panel p-3 font-mono text-xs text-ink">
        {error.message === '' ? error.name : error.message}
      </pre>
      <div className="mt-3">
        <Button size="sm" onClick={onRetry}>
          다시 그리기
        </Button>
      </div>
    </div>
  );
}
